/**
 * useSwal — wrapper around SweetAlert2 (vendor lib loaded via index.html)
 * Provides branded confirm dialogs and alerts that match the Materio theme.
 */

declare const Swal: any;

export function useSwal() {
  return {
    /** Show a confirmation dialog. Returns true if user confirmed. */
    confirm: async (title: string, text?: string): Promise<boolean> => {
      const result = await Swal.fire({
        title,
        text,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Confirm',
        cancelButtonText: 'Cancel',
        customClass: {
          confirmButton: 'np-btn np-btn-primary me-2',
          cancelButton: 'np-btn np-btn-ghost',
        },
        buttonsStyling: false,
      });
      return result.isConfirmed;
    },

    /** Show a destructive (delete) confirmation dialog. Returns true if confirmed. */
    confirmDelete: async (itemName?: string): Promise<boolean> => {
      const result = await Swal.fire({
        title: 'Are you sure?',
        text: itemName ? `Delete "${itemName}"? This cannot be undone.` : 'This action cannot be undone.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Delete',
        cancelButtonText: 'Cancel',
        customClass: {
          confirmButton: 'np-btn np-btn-danger me-2',
          cancelButton: 'np-btn np-btn-ghost',
        },
        buttonsStyling: false,
      });
      return result.isConfirmed;
    },

    /** Show a success alert */
    success: (title: string, text?: string) =>
      Swal.fire({
        title,
        text,
        icon: 'success',
        customClass: { confirmButton: 'np-btn np-btn-primary' },
        buttonsStyling: false,
      }),

    /** Show an error alert */
    error: (title: string, text?: string) =>
      Swal.fire({
        title,
        text,
        icon: 'error',
        customClass: { confirmButton: 'np-btn np-btn-primary' },
        buttonsStyling: false,
      }),

    /**
     * Show a multi-item error list in a proper, non-auto-dismissing modal - for a validation
     * response with several distinct problems (e.g. "Step 2: X is required. Step 5: Y is
     * required. ..."), which a transient toast truncates/clips before it can be read (confirmed
     * live: a use case submission with 5+ outstanding fields).
     */
    errorList: (title: string, items: string[]) => {
      const escape = (s: string) =>
        s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      return Swal.fire({
        title,
        html: `<ul style="text-align:left; padding-left:1.25rem; margin:0;">${
          items.map((i) => `<li style="margin-bottom:0.35rem;">${escape(i)}</li>`).join('')
        }</ul>`,
        icon: 'error',
        customClass: { confirmButton: 'np-btn np-btn-primary' },
        buttonsStyling: false,
      });
    },
  };
}
